import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const DashboardPage = () => {
    const [stats, setStats] = useState(null);
    const [postStats, setPostStats] = useState(null);
    const [recentUsers, setRecentUsers] = useState([]);
    const [recentPosts, setRecentPosts] = useState([]);
    const [topPosts, setTopPosts] = useState([]);
    const [topUsers, setTopUsers] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('token');
                const headers = { Authorization: `Bearer ${token}` };

                const [statsRes, postStatsRes, recentUsersRes, recentPostsRes, topPostsRes, topUsersRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/api/admin/stats`, { headers }),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/admin/stats/posts`, { headers }),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users/recent`, { headers }),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/admin/posts/recent`, { headers }),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/admin/stats/posts/top`, { headers }),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/admin/stats/users/top`, { headers }),
                ]);

                setStats(statsRes.data);
                setPostStats(postStatsRes.data);
                setRecentUsers(recentUsersRes.data);
                setRecentPosts(recentPostsRes.data);
                setTopPosts(topPostsRes.data);
                setTopUsers(topUsersRes.data);
            } catch (err) {
                setError('Failed to fetch dashboard data');
            }
        };

        fetchData();
    }, []);

    if (error) {
        return <div className="text-red-500">{error}</div>;
    }

    if (!stats || !postStats) {
        return <div>Loading...</div>;
    }

    // Data for charts
    const postLikesData = {
        labels: topPosts.map(post => post.content.substring(0, 20) + '...'),
        datasets: [
            {
                label: 'Likes',
                data: topPosts.map(post => post.likes),
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
            },
        ],
    };

    const userPostsData = {
        labels: topUsers.map(user => user.username),
        datasets: [
            {
                label: 'Posts',
                data: topUsers.map(user => user.post_count || 0),
                backgroundColor: 'rgba(153, 102, 255, 0.6)',
            },
        ],
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Dashboard</h1>
                <p className="text-slate-500 mt-1">Welcome back! Here's your platform overview.</p>
            </div>
            
            <div>
                <h2 className="text-lg font-semibold text-slate-800 mb-4">User Statistics</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="stat-card">
                        <p className="text-slate-600 font-medium mb-2">Total Users</p>
                        <p className="text-3xl lg:text-4xl font-bold text-blue-600">{stats.total_users}</p>
                        <p className="text-xs text-slate-500 mt-3">Active members</p>
                    </div>
                    <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                        <p className="text-slate-600 font-medium mb-2">Online Users</p>
                        <p className="text-3xl lg:text-4xl font-bold text-green-600">{stats.online_users}</p>
                        <p className="text-xs text-slate-500 mt-3">Right now</p>
                    </div>
                    <div className="stat-card" style={{ borderLeftColor: '#f59e0b' }}>
                        <p className="text-slate-600 font-medium mb-2">24 Hours Active</p>
                        <p className="text-3xl lg:text-4xl font-bold text-amber-600">{stats.daily_active_users}</p>
                        <p className="text-xs text-slate-500 mt-3">Daily active</p>
                    </div>
                    <div className="stat-card" style={{ borderLeftColor: '#8b5cf6' }}>
                        <p className="text-slate-600 font-medium mb-2">Monthly Active</p>
                        <p className="text-3xl lg:text-4xl font-bold text-purple-600">{stats.monthly_active_users}</p>
                        <p className="text-xs text-slate-500 mt-3">This month</p>
                    </div>
                </div>
            </div>

            <div>
                <h2 className="text-lg font-semibold text-slate-800 mb-4">Content Statistics</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="stat-card">
                        <p className="text-slate-600 font-medium mb-2">Total Posts</p>
                        <p className="text-3xl lg:text-4xl font-bold text-blue-600">{postStats.total_posts}</p>
                        <p className="text-xs text-slate-500 mt-3">All time</p>
                    </div>
                    <div className="stat-card" style={{ borderLeftColor: '#ef4444' }}>
                        <p className="text-slate-600 font-medium mb-2">Total Likes</p>
                        <p className="text-3xl lg:text-4xl font-bold text-red-600">{postStats.total_likes}</p>
                        <p className="text-xs text-slate-500 mt-3">Community engagement</p>
                    </div>
                    <div className="stat-card" style={{ borderLeftColor: '#06b6d4' }}>
                        <p className="text-slate-600 font-medium mb-2">Total Comments</p>
                        <p className="text-3xl lg:text-4xl font-bold text-cyan-600">{postStats.total_comments}</p>
                        <p className="text-xs text-slate-500 mt-3">Discussions</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                    <h2 className="text-lg font-semibold text-slate-800 mb-4">Top Posts by Likes</h2>
                    <div className="card p-6">
                        <div style={{ height: '300px' }}>
                            <Bar data={postLikesData} options={{ 
                                maintainAspectRatio: false,
                                plugins: { legend: { position: 'top' } },
                                scales: { y: { beginAtZero: true } }
                            }} />
                        </div>
                    </div>
                </div>

                <div>
                    <h2 className="text-lg font-semibold text-slate-800 mb-4">Top Users by Posts</h2>
                    <div className="card p-6">
                        <div style={{ height: '300px' }}>
                            <Bar data={userPostsData} options={{ 
                                maintainAspectRatio: false,
                                plugins: { legend: { position: 'top' } },
                                scales: { y: { beginAtZero: true } }
                            }} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                    <h2 className="text-lg font-semibold text-slate-800 mb-4">Recent Users</h2>
                    <div className="card overflow-hidden">
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="text-left">User</th>
                                    <th className="text-right">Last Seen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentUsers.map(user => (
                                    <tr key={user.id}>
                                        <td>
                                            <div>
                                                <p className="font-medium text-slate-900">{user.name}</p>
                                                <p className="text-xs text-slate-500">@{user.username}</p>
                                            </div>
                                        </td>
                                        <td className="text-right text-sm text-slate-600">
                                            {new Date(user.last_seen).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div>
                    <h2 className="text-lg font-semibold text-slate-800 mb-4">Recent Posts</h2>
                    <div className="card overflow-hidden">
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="text-left">Content</th>
                                    <th className="text-left">Author</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentPosts.map(post => (
                                    <tr key={post.id}>
                                        <td>
                                            <p className="text-sm text-slate-800 line-clamp-2">{post.content.substring(0, 60)}...</p>
                                        </td>
                                        <td>
                                            <p className="text-sm text-slate-600">@{post.author_username}</p>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;